/**
 * Display formatters.
 *
 * Per `tnn-engineering-conventions.md` and the design language:
 *   - Money values are Decimal-as-string from the backend (`"4300.00"`).
 *     Convert to display string with proper formatting at render time.
 *   - Numbers should always render with `tabular-nums` (CSS), but the
 *     numeric grouping (commas) happens in JS via `Intl.NumberFormat`.
 *   - Dates render via `date-fns` for control over format strings.
 *
 * Centralizing these makes the daily-loop loop easier: when Sam reports
 * "currency shows as 4300.00 not $4,300", the fix lives in ONE place.
 */
import { format as fmt } from "date-fns";

const USD_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const USD_WHOLE_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const NUMBER_FORMATTER = new Intl.NumberFormat("en-US");

/**
 * Format a money string ("4300.00") as USD currency ("$4,300.00").
 *
 * Pass `{ short: true }` to drop cents when they're zero ($4,300 vs $4,300.00) -
 * useful in dashboard tiles where space is tight.
 */
export function formatMoney(
  amount: string | number | null | undefined,
  options: { short?: boolean } = {},
): string {
  if (amount === null || amount === undefined || amount === "") return "-";
  const n = typeof amount === "string" ? Number.parseFloat(amount) : amount;
  if (Number.isNaN(n)) return "-";
  if (options.short && n === Math.trunc(n)) {
    return USD_WHOLE_FORMATTER.format(n);
  }
  return USD_FORMATTER.format(n);
}

/**
 * Format a number with thousands separators. "12847" → "12,847".
 */
export function formatNumber(n: number | null | undefined): string {
  if (n === null || n === undefined) return "-";
  return NUMBER_FORMATTER.format(n);
}

/**
 * Format a date ISO string in the standard TNN display form.
 *
 * Default: "Mar 15, 2026"
 * Pass `format: "short"` for "3/15/26"
 * Pass `format: "long"` for "March 15, 2026"
 */
export function formatDate(
  isoDate: string | Date | null | undefined,
  format: "short" | "default" | "long" = "default",
): string {
  if (!isoDate) return "-";
  const d = typeof isoDate === "string" ? new Date(isoDate) : isoDate;
  if (Number.isNaN(d.getTime())) return "-";
  switch (format) {
    case "short":
      return fmt(d, "M/d/yy");
    case "long":
      return fmt(d, "MMMM d, yyyy");
    default:
      return fmt(d, "MMM d, yyyy");
  }
}

/**
 * Compact relative time. "2h ago", "3d ago", "Jan 5". Used in activity feeds.
 */
export function formatRelative(isoDate: string | Date | null | undefined): string {
  if (!isoDate) return "-";
  const d = typeof isoDate === "string" ? new Date(isoDate) : isoDate;
  if (Number.isNaN(d.getTime())) return "-";

  const now = Date.now();
  const diffMs = now - d.getTime();
  const diffMin = Math.floor(diffMs / (60 * 1000));
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return formatDate(d);
}

/** Time only, e.g. "12:00 PM". */
export function formatTime(
  isoDate: string | Date | null | undefined,
): string {
  if (!isoDate) return "-";
  const d = typeof isoDate === "string" ? new Date(isoDate) : isoDate;
  if (Number.isNaN(d.getTime())) return "-";
  return fmt(d, "h:mm a");
}

/** Date plus time range, e.g. "Mar 15, 2026 · 12:00 PM – 4:00 PM". */
export function formatTimeRange(
  startIso: string | Date | null | undefined,
  endIso: string | Date | null | undefined,
): string {
  if (!startIso) return "-";
  const datePart = formatDate(startIso);
  const start = formatTime(startIso);
  const end = formatTime(endIso);
  if (end === "-") {
    return `${datePart} · ${start}`;
  }
  return `${datePart} · ${start} – ${end}`;
}
